import React from 'react';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';

const getYouTubeEmbedUrl = (url) => {
  if (!url) return null;
  try {
    // Already an embed URL
    if (url?.includes('youtube.com/embed/')) return url;
    
    let videoId = null;
    
    // youtu.be/VIDEO_ID
    const shortMatch = url?.match(/youtu\.be\/([a-zA-Z0-9_-]{11})/);
    if (shortMatch) videoId = shortMatch?.[1];
    
    // youtube.com/watch?v=VIDEO_ID
    const watchMatch = url?.match(/[?&]v=([a-zA-Z0-9_-]{11})/);
    if (watchMatch) videoId = watchMatch?.[1];
    
    // youtube.com/shorts/VIDEO_ID
    const shortsMatch = url?.match(/youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/);
    if (shortsMatch) videoId = shortsMatch?.[1];

    if (videoId) return `https://www.youtube.com/embed/${videoId}`;
    return null;
  } catch {
    return null;
  }
};

const SurpriseEditor = ({ content, onChange }) => {
  const handleChange = (field, value) => {
    onChange({
      ...content,
      [field]: value
    });
  };

  const surpriseTypes = [
    { value: 'video', label: 'Video İçerik' },
    { value: 'image', label: 'Görsel İçerik' },
    { value: 'quiz', label: 'Hızlı Quiz' },
    { value: 'story', label: 'Hikaye' },
    { value: 'challenge', label: 'Özel Meydan Okuma' },
    { value: 'custom', label: 'Özel İçerik' }
  ];

  const embedUrl = getYouTubeEmbedUrl(content?.videoUrl || '');
  const isValidYouTube = content?.videoUrl ? !!embedUrl : true;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
          <Icon name="Sparkles" size={20} color="var(--color-accent)" />
        </div>
        <div>
          <h4 className="text-base md:text-lg font-heading font-semibold text-foreground">
            Sürpriz Günü İçeriği
          </h4>
          <p className="caption text-muted-foreground">
            Kullanıcıları şaşırtacak özel içerik oluşturun
          </p>
        </div>
      </div>
      <Select
        label="Sürpriz Tipi"
        options={surpriseTypes}
        value={content?.surpriseType || ''}
        onChange={(value) => handleChange('surpriseType', value)}
        placeholder="Sürpriz içerik tipini seçin"
        required
        description="İçeriğinizin formatını belirleyin"
      />
      <Input
        label="Sürpriz Başlığı"
        type="text"
        placeholder="Örn: Bugün Sana Özel Bir Sürpriz!"
        value={content?.title || ''}
        onChange={(e) => handleChange('title', e?.target?.value)}
        required
        description="Merak uyandıran bir başlık"
      />
      <div>
        <label className="block text-sm font-medium text-foreground mb-2">
          Sürpriz İçeriği
          <span className="text-destructive ml-1">*</span>
        </label>
        <textarea
          className="flex w-full rounded-lg border border-input bg-input px-4 py-3 text-sm focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring focus-visible:ring-offset-3 disabled:cursor-not-allowed disabled:opacity-50 min-h-40 md:min-h-48"
          placeholder="Sürpriz içeriğinizi detaylı olarak yazın..."
          value={content?.content || ''}
          onChange={(e) => handleChange('content', e?.target?.value)}
          required
        />
        <p className="caption text-muted-foreground mt-2">
          Seçtiğiniz sürpriz tipine uygun içerik hazırlayın
        </p>
      </div>
      {content?.surpriseType === 'video' && (
        <div className="space-y-3">
          <Input
            label="YouTube Video URL"
            type="url"
            placeholder="https://youtube.com/watch?v=... veya https://youtu.be/..."
            value={content?.videoUrl || ''}
            onChange={(e) => handleChange('videoUrl', e?.target?.value)}
            description="YouTube video linki (watch, youtu.be veya shorts formatı desteklenir)"
          />
          {content?.videoUrl && !isValidYouTube && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-destructive/10 border border-destructive/20">
              <Icon name="AlertCircle" size={16} color="var(--color-destructive)" />
              <p className="text-sm text-destructive">
                Geçersiz YouTube linki. Lütfen geçerli bir YouTube URL'si girin.
              </p>
            </div>
          )}
          {embedUrl && (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Icon name="CheckCircle" size={16} color="var(--color-success)" />
                <p className="text-sm text-success font-medium">Video bağlantısı geçerli</p>
              </div>
              <div className="relative w-full rounded-lg overflow-hidden border border-border" style={{ paddingBottom: '56.25%' }}>
                <iframe
                  src={embedUrl}
                  title="YouTube Video Önizleme"
                  className="absolute inset-0 w-full h-full"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
              <p className="caption text-muted-foreground">Video önizlemesi</p>
            </div>
          )}
        </div>
      )}
      {content?.surpriseType === 'image' && (
        <Input
          label="Görsel URL"
          type="url"
          placeholder="https://example.com/image.jpg"
          value={content?.imageUrl || ''}
          onChange={(e) => handleChange('imageUrl', e?.target?.value)}
          description="Görselin direkt linki"
        />
      )}
      {content?.surpriseType === 'quiz' && (
        <div className="space-y-4">
          <Input
            label="Quiz Sorusu"
            type="text"
            placeholder="Hızlı bir soru sorun..."
            value={content?.quizQuestion || ''}
            onChange={(e) => handleChange('quizQuestion', e?.target?.value)}
          />
          <Input
            label="Doğru Cevap"
            type="text"
            placeholder="Doğru cevabı girin..."
            value={content?.quizAnswer || ''}
            onChange={(e) => handleChange('quizAnswer', e?.target?.value)}
          />
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="p-4 rounded-lg bg-accent/10 border border-accent/20">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="Zap" size={16} color="var(--color-accent)" />
            <span className="text-sm font-medium text-foreground">Sürpriz Fikirleri</span>
          </div>
          <ul className="caption text-muted-foreground space-y-1">
            <li>• Özel indirim kodu</li>
            <li>• Eğlenceli video</li>
            <li>• İlginç trafik bilgisi</li>
            <li>• Mini oyun</li>
          </ul>
        </div>

        <div className="p-4 rounded-lg bg-muted border border-border">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="Star" size={16} color="var(--color-accent)" />
            <span className="text-sm font-medium text-foreground">Özel Gün</span>
          </div>
          <p className="caption text-muted-foreground">
            Sürpriz günleri özel günlerde veya kampanyalarda kullanın
          </p>
        </div>
      </div>
      <div className="p-4 md:p-6 rounded-lg bg-accent/10 border border-accent/20">
        <div className="flex items-start gap-3">
          <Icon name="Info" size={20} color="var(--color-accent)" className="flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-foreground font-medium mb-1">
              Yaratıcı Olun
            </p>
            <p className="caption text-muted-foreground">
              Sürpriz günleri, kullanıcılarınızı şaşırtmak ve etkileşimi artırmak için mükemmel bir fırsattır. Beklenmedik ve değerli içerikler sunun.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SurpriseEditor;